import React, { useState, useEffect, useRef } from "react";
import {
  FiSearch,
  FiFilter,
  FiUser,
  FiArrowLeft,
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight,
  FiSend,
  FiPlus,
  FiDownload,
} from "react-icons/fi";
import {
  BsWhatsapp,
  BsThreeDotsVertical,
  BsCheck2All,
  BsExclamationCircle,
  BsCheck,
} from "react-icons/bs";
import { FaRegFileAlt, FaImage, FaVideo } from "react-icons/fa";
import apiEndpoints from "../../apiconfig";
// Chat List Component
const ChatList = React.memo(
  ({
    wabaNumber,
    searchQuery,
    onSearchChange,
    filteredChats,
    selectedChat,
    handleChatSelect,
    formatTime,
    filter,
    setFilter,
    loadingChats,
  }) => (
    <div className="w-full lg:w-96 bg-white border-r border-gray-200 flex flex-col h-full font-['Montserrat'] shadow-sm">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1f8c55] to-[#25D366] text-white px-6 py-5">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-xl font-semibold font-['Montserrat']">
            Business Inbox
          </h1>
          <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            <span className="text-xs font-medium font-['Montserrat']">
              LIVE
            </span>
          </div>
        </div>
        <p className="text-white/80 text-sm font-['Montserrat']">
          WABA: {wabaNumber}
        </p>
      </div>

      {/* Search Bar */}
      <div className="p-4 bg-gray-50/80 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Recent Chats
          </span>

          <button
            onClick={() => setFilter(filter === "all" ? "inbound" : "all")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold uppercase ${
              filter === "inbound"
                ? "bg-[#25D366] text-white border-[#25D366]"
                : "bg-white text-gray-600 border-gray-200"
            }`}
          >
            <FiFilter />
            {filter === "inbound" ? "Inbound" : "Filter"}
          </button>
        </div>

        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
          <input
            type="text"
            placeholder="Search..."
            className="w-full pl-10 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none transition-all font-['Montserrat']"
            value={searchQuery}
            onChange={onSearchChange}
          />
          {/* <FiFilter className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer" /> */}
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto bg-white">
        {loadingChats ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-[#25D366] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-gray-500 mt-3">Loading chats...</p>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-gray-400 py-12">
            <FiMessageSquare className="text-4xl mb-3 opacity-50" />
            <p className="text-sm font-medium font-['Montserrat']">
              No conversations found
            </p>
          </div>
        ) : (
          <>
            {filteredChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => handleChatSelect(chat)}
                className={`p-4 cursor-pointer border-b border-gray-100 transition-all duration-200 ${
                  selectedChat?.id === chat.id
                    ? "bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-[#25D366]"
                    : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-[#1f8c55] rounded-full flex items-center justify-center flex-shrink-0">
                    <FiUser className="text-white text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-semibold text-gray-900 truncate text-sm font-['Montserrat']">
                        {chat.contact_name || chat.mobile_number}
                      </h3>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-2 font-['Montserrat']">
                        {chat.last_message_time
                          ? formatTime(chat.last_message_time)
                          : ""}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 truncate leading-relaxed font-['Montserrat']">
                      {chat.last_message || "Start a conversation"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  ),
);
const getMediaType = (msg) => {
  // 1️⃣ Prefer mime_type if present
  if (msg.mime_type) {
    if (msg.mime_type.startsWith("image")) return "image";
    if (msg.mime_type.startsWith("video")) return "video";
    if (msg.mime_type.startsWith("audio")) return "audio";
  }

  // 2️⃣ Fallback: detect from file extension
  if (msg.media_url) {
    const url = msg.media_url.toLowerCase();
    if (url.match(/\.(jpg|jpeg|png|gif|webp)$/)) return "image";
    if (url.match(/\.(mp4|webm|ogg)$/)) return "video";
    if (url.match(/\.(mp3|wav|ogg)$/)) return "audio";
  }

  return "document";
};

const renderMediaMessage = (msg) => {
  if (!msg.media_url) return null;

  const type = getMediaType(msg);

  const downloadLink = (
    <a
      href={msg.media_url}
      download
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 mt-2 text-[10px] font-bold text-[#1f8c55] hover:text-[#25D366] transition-colors uppercase tracking-wider"
    >
      <FiDownload className="text-sm" /> Download {type}
    </a>
  );

  if (type === "image") {
    return (
      <div className="flex flex-col">
        <img
          src={msg.media_url}
          alt="media"
          className="mt-2 rounded-lg w-full max-h-80 object-contain shadow-sm border border-gray-100"
        />
        {downloadLink}
      </div>
    );
  }

  if (type === "video") {
    return (
      <div className="flex flex-col">
        <video controls className="mt-2 rounded-lg w-full max-h-80 shadow-sm border border-gray-100">
          <source src={msg.media_url} />
        </video>
        {downloadLink}
      </div>
    );
  }

  if (type === "audio") {
    return (
      <div className="flex flex-col">
        <audio controls className="mt-2 w-full max-w-[240px]">
          <source src={msg.media_url} />
        </audio>
        {downloadLink}
      </div>
    );
  }

  return (
    <a
      href={msg.media_url}
      download
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 mt-2 border border-gray-100 rounded-xl bg-gray-50/50 hover:bg-white hover:shadow-md transition-all group"
    >
      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
        <FaRegFileAlt className="text-orange-600 text-lg" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">Document</p>
        <p className="text-[10px] text-gray-500 uppercase tracking-tighter">Click to download</p>
      </div>
      <FiDownload className="text-gray-400 group-hover:text-[#25D366] transition-colors text-lg" />
    </a>
  );
};


// Chat View Component
const ChatView = ({
  selectedChat,
  isMobileView,
  handleBackToChatList,
  messages,
  loadingMessages,
  messageText,
  onMessageChange,
  showAttachMenu,
  setShowAttachMenu,
  formatTime,
  messagesContainerRef,
  messagesEndRef,
  renderTemplateMessage,
  sendSessionMessage,
  imageInputRef,
  docInputRef,
  handleMediaSend,
  mediaType,
  setMediaType,
  sendingMessage,
}) => {
  const canSendFreeText = selectedChat?.can_free_text;

  return (
    <div className="flex-1 flex flex-col bg-white h-full font-['Montserrat']">
      {selectedChat ? (
        <>
          {/* Chat Header */}
          <div className="px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {isMobileView && (
                  <button
                    onClick={handleBackToChatList}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiArrowLeft className="text-gray-600 text-xl" />
                  </button>
                )}
                <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-[#1f8c55] rounded-full flex items-center justify-center">
                  <FiUser className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900 text-lg font-['Montserrat']">
                    {selectedChat.contact_name || selectedChat.mobile_number}
                  </h2>
                  <div className="flex items-center space-x-1">
                    {/* <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span> */}
                    <p className="text-xs text-gray-500 font-['Montserrat']">
                      {selectedChat.contact_name
                        ? selectedChat.mobile_number
                        : ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div
            ref={messagesContainerRef}
            className="flex-1 p-6 overflow-y-auto bg-gradient-to-b from-gray-50 to-white"
          >
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-4 border-[#25D366] border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs text-gray-500 font-medium">
                    Loading messages…
                  </p>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <div className="bg-gray-100 p-6 rounded-2xl mb-4">
                  <BsWhatsapp className="text-gray-300 text-4xl" />
                </div>
                <p className="text-sm font-medium text-gray-500 font-['Montserrat']">
                  No messages yet
                </p>
                <p className="text-xs text-gray-400 mt-1 font-['Montserrat']">
                  Start the conversation
                </p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-4">
                {messages.map((msg) => {
                  const isOutgoing = msg.isSent === 1;

                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOutgoing ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md rounded-2xl px-4 py-3 shadow-sm ${isOutgoing
                          ? "bg-[#DCF8C8] text-black rounded-br-sm"
                          : "bg-white text-black border border-gray-200 rounded-bl-sm"
                          }${msg.media_url ? "p-2" : "px-4 py-3"} `}
                      >
                        {msg.message_type === "template" &&
                          msg.template_payload ? (
                          renderTemplateMessage(msg)
                        ) : msg.media_url ? (
                          renderMediaMessage(msg)
                        ) : (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words text-black">
                            {msg.message}
                          </p>
                        )}

                        <div className="flex items-center justify-end space-x-1 mt-2">
                          <span
                            className={`text-xs ${isOutgoing ? "text-black" : "text-gray-500"}`}
                          >
                            {formatTime(msg.timestamp)}
                          </span>

                          {isOutgoing && (
                            <>
                              {msg.status?.toLowerCase() === "failed" && (
                                <BsExclamationCircle className="text-red-500 text-sm" />
                              )}
                              {msg.status?.toLowerCase() === "sent" && (
                                <BsCheck className="text-gray-500 text-sm" />
                              )}
                              {msg.status?.toLowerCase() === "delivered" && (
                                <BsCheck2All className="text-gray-500 text-sm" />
                              )}
                              {msg.status?.toLowerCase() === "read" && (
                                <BsCheck2All className="text-[#34B7F1] text-sm" />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* WhatsApp-style Chat Footer */}
          <div className="relative px-4 py-3 bg-[#f0f2f5] border-t border-gray-200">
            {/* Attachment Menu */}
            {showAttachMenu && (
              <div className="absolute bottom-16 left-4 bg-white rounded-2xl shadow-xl p-3 w-48 z-50">
                <button
                  onClick={() => imageInputRef.current.click()}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                >
                  <FaImage className="text-blue-500 text-lg" />
                  Image
                </button>

                <button
                  onClick={() => docInputRef.current.click()}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                >
                  <FaRegFileAlt className="text-orange-500 text-lg" />
                  Document
                </button>
              </div>
            )}

            {/* Footer Row */}
            <div className="flex items-center gap-3">
              {/* + Button */}
              <button
                onClick={() => setShowAttachMenu(!showAttachMenu)}
                className="p-2 rounded-full hover:bg-gray-200 transition"
              >
                <FiPlus className="text-gray-600 text-xl" />
              </button>

              {/* Message Input */}
              <textarea
                rows={1}

                placeholder={
                  canSendFreeText
                    ? sendingMessage
                      ? "Sending..."
                      : "Type a message"
                    : "24-hour window closed. Use template message."
                }
                value={messageText}
                onChange={onMessageChange}
                disabled={!canSendFreeText || sendingMessage}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.shiftKey) {
                    // Allow new line
                    return;
                  }

                  if (e.key === "Enter" && !e.shiftKey && !sendingMessage) {
                    e.preventDefault();
                    sendSessionMessage();
                  }
                }}

                className={`flex-1 px-4 py-2.5 rounded-2xl border resize-none max-h-32 overflow-y-auto

    ${canSendFreeText && !sendingMessage
                    ? "bg-white border-gray-300 focus:ring-2 focus:ring-[#25D366]"
                    : "bg-gray-100 border-gray-200 cursor-not-allowed"
                  }
    text-sm focus:outline-none`}
              />

              {/* IMAGE INPUT */}
              <input
                type="file"
                ref={imageInputRef}
                hidden
                accept="image/*"
                onChange={(e) => handleMediaSend(e, "image")}
              />

              {/* DOCUMENT INPUT */}
              <input
                type="file"
                ref={docInputRef}
                hidden
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleMediaSend(e, "document")}
              />

              {/* Send Button */}
              <button
                onClick={sendSessionMessage}
                disabled={
                  !canSendFreeText || !messageText.trim() || sendingMessage
                }
                className={`p-3 rounded-full transition flex items-center justify-center ${canSendFreeText && messageText.trim() && !sendingMessage
                  ? "bg-[#25D366] hover:bg-[#1f8c55] text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
              >
                {sendingMessage ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FiSend />
                )}
              </button>
            </div>
          </div>
        </>
      ) : (
        // Empty State
        <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white p-8">
          <div className="bg-gradient-to-br from-[#25D366] to-[#1f8c55] p-8 rounded-3xl shadow-lg mb-6">
            <BsWhatsapp className="text-white text-5xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center font-['Montserrat']">
            Business Communication Console
          </h2>
          <p className="text-gray-500 text-center max-w-md leading-relaxed font-['Montserrat']">
            Select a conversation from the sidebar to view messages and manage
            customer communications.
          </p>
        </div>
      )}
    </div>
  );
};

const LiveChat = () => {
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const activeChatRef = useRef(null);
  const [messageText, setMessageText] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [mediaType, setMediaType] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingChats, setLoadingChats] = useState(false);  
const [filter, setFilter] = useState("all"); // 'all' or 'inbound'

  const onSearchChange = React.useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const onMessageChange = React.useCallback((e) => {
    setMessageText(e.target.value);
  }, []);

  const [wabaNumber, setWabaNumber] = useState("");
  // Responsive screen detection
  useEffect(() => {
    const checkScreen = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobileView(mobile);
      if (!mobile) setShowChat(false);
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);

    // ✅ Load WABA number from localStorage safely
    const storedWaba = localStorage.getItem("waba_number");
    if (storedWaba) {
      setWabaNumber(storedWaba);
    }

    // ✅ Fetch chat list
    const token = localStorage.getItem("token");
setLoadingChats(true);
    fetch(`${apiEndpoints.chat_history}?history=24hrs&filter=${filter}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setChats(data.data);
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
      })
      .finally(() => {
        setLoadingChats(false);
      });

    return () => window.removeEventListener("resize", checkScreen);
  }, [filter]);

  const sendSessionMessage = async () => {
    if (!messageText.trim()) return;
    if (!selectedChat?.can_free_text) return;
    if (sendingMessage) return; // 🚫 prevent double send

    const token = localStorage.getItem("token");
    setSendingMessage(true);

    try {
      await fetch(apiEndpoints.send_session_message, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          to: selectedChat.mobile_number,
          text: messageText,
        }),
      });

      setMessageText("");
      handleChatSelect(selectedChat); // refresh messages
    } catch (err) {
      console.error("Send message error:", err);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleMediaSend = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedChat?.can_free_text) return;
    if (sendingMessage) return;
    setShowAttachMenu(false);
    const token = localStorage.getItem("token");
    const formData = new FormData();

    formData.append("to", selectedChat.mobile_number);
    formData.append("file", file);
    formData.append("media_type", type); // optional but useful

    setSendingMessage(true); // 🔥 START LOADING

    try {
      await fetch(apiEndpoints.send_session_message, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      // ✅ CLOSE ATTACH MENU
      setShowAttachMenu(false);

      // ✅ REFRESH CHAT (SAME AS TEXT MESSAGE)
      await handleChatSelect(selectedChat);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err) {
      console.error("Media send error", err);
    } finally {
      e.target.value = "";
      setSendingMessage(false); // 🔥 STOP LOADING
    }
  };


  // ✅ Select chat & load messages
  const handleChatSelect = async (chat) => {
    setSelectedChat(chat);
    // setMessages([]); // clear old messages
    setLoadingMessages(true); // start loader
    activeChatRef.current = chat.mobile_number;

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${apiEndpoints.chat_messages}?history=24hrs&mobile_number=${chat.mobile_number}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      // ⛔ Ignore stale responses (important when clicking fast)
      if (activeChatRef.current !== chat.mobile_number) return;

      if (data.status && Array.isArray(data.data)) {
        setMessages([...data.data].reverse());
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error("Message fetch error:", err);
      setMessages([]);
    } finally {
      // ✅ ALWAYS stop loading
      if (activeChatRef.current === chat.mobile_number) {
        setLoadingMessages(false);
      }
    }

    if (isMobileView) setShowChat(true);
  };

  const handleBackToChatList = () => {
    setShowChat(false);
    setSelectedChat(null);
    setMessages([]);
  };
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [messages, selectedChat]);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(delay);
  }, [searchQuery]);

  // Filter chats based on search
  const filteredChats = chats.filter(
    (chat) =>
      chat.contact_name?.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
      chat.mobile_number?.includes(debouncedQuery),
  );

  // Format timestamp
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderTemplateMessage = (msg) => {
    const payload = msg.template_payload;
    const isMsgOutgoing = msg.isSent === 1;

    // ✅ Fallback for old messages
    if (!payload || !payload.body || !payload.body.text) {
      return (
        <p className="text-sm whitespace-pre-wrap break-words text-black">
          {msg.message}
        </p>
      );
    }

    let renderedText = payload.body.text;

    // ✅ Replace variables {{1}}, {{2}} ...
    if (Array.isArray(payload.variables)) {
      payload.variables.forEach((v, i) => {
        renderedText = renderedText.replace(`{{${i + 1}}}`, v);
      });
    }

    return (
      <div className="space-y-2">
        {/* HEADER */}
        {payload.header?.type === "text" && (
          <p className="font-semibold text-white">{payload.header.text}</p>
        )}

        {payload.header?.type === "image" && payload.header.link && (
          <img
            src={payload.header.link}
            alt="template-header"
            className="rounded-lg max-w-full"
          />
        )}

        {payload.header?.type === "document" && payload.header.link && (
          <a
            href={payload.header.link}
            download={payload.header.filename || "document.pdf"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 mt-2 border border-gray-100 rounded-xl bg-gray-50/50 hover:bg-white hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
              <FaRegFileAlt className="text-orange-600 text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {payload.header.filename || "Document.pdf"}
              </p>
              <p className="text-[10px] text-gray-500 uppercase tracking-tighter">Click to download</p>
            </div>
            <FiDownload className="text-gray-400 group-hover:text-[#25D366] transition-colors text-lg" />
          </a>
        )}

        {payload.header?.type === "video" && payload.header.link && (
          <video controls className="mt-2 rounded-lg w-full max-h-80 shadow-sm border border-gray-100">
            <source src={payload.header.link} />
          </video>
        )}

        {payload.header?.type === "audio" && payload.header.link && (
          <audio controls className="mt-2 w-full max-w-[240px]">
            <source src={payload.header.link} />
          </audio>
        )}

        {/* BODY */}
        <p className="text-black text-sm whitespace-pre-wrap">{renderedText}</p>

        {/* BUTTONS */}
        {Array.isArray(payload.buttons) && payload.buttons.length > 0 && (
          <div className="flex flex-col gap-2 mt-2">
            {payload.buttons.map((btn, i) => {
              if (btn.type === "URL") {
                return (
                  <a
                    key={i}
                    href={btn.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm py-2 px-4 rounded-lg font-semibold shadow-sm transition-all text-center block ${
                      isMsgOutgoing
                        ? "bg-white text-green-700 hover:bg-gray-50 border border-green-200"
                        : "bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300"
                    }`}
                  >
                    {btn.text}
                  </a>
                );
              }

              if (btn.type === "QUICK_REPLY" || btn.type === "button") {
                return (
                  <button
                    key={i}
                    className={`text-sm py-2 px-4 rounded-lg font-semibold shadow-sm transition-all text-center ${
                      isMsgOutgoing
                        ? "bg-white text-green-700 hover:bg-gray-50 border border-green-200"
                        : "bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300"
                    }`}
                  >
                    {btn.text}
                  </button>
                );
              }

              return null;
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`bg-gray-50 font-['Montserrat'] ${
        isMobileView ? "h-screen" : "h-[calc(100vh-125px)]"
      }`}
    >
      {isMobileView ? (
        <div className="flex flex-col h-full">
          {showChat ? (
            <ChatView
              selectedChat={selectedChat}
              isMobileView={isMobileView}
              handleBackToChatList={handleBackToChatList}
              messages={messages}
              loadingMessages={loadingMessages}
              messageText={messageText}
              onMessageChange={onMessageChange}
              showAttachMenu={showAttachMenu}
              setShowAttachMenu={setShowAttachMenu}
              formatTime={formatTime}
              messagesContainerRef={messagesContainerRef}
              messagesEndRef={messagesEndRef}
              renderTemplateMessage={renderTemplateMessage}
              sendSessionMessage={sendSessionMessage}
              imageInputRef={imageInputRef}
              docInputRef={docInputRef}
              handleMediaSend={handleMediaSend}
              mediaType={mediaType}
              setMediaType={setMediaType}
              sendingMessage={sendingMessage}
            />
          ) : (
            <ChatList
              wabaNumber={wabaNumber}
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              filteredChats={filteredChats}
              selectedChat={selectedChat}
              handleChatSelect={handleChatSelect}
              formatTime={formatTime}
              filter={filter}
              setFilter={setFilter}
              loadingChats={loadingChats}
            />
          )}
        </div>
      ) : (
        <div className="flex h-full rounded-2xl overflow-hidden shadow-lg border border-gray-200 m-4">
          <ChatList
            wabaNumber={wabaNumber}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            filteredChats={filteredChats}
            selectedChat={selectedChat}
            handleChatSelect={handleChatSelect}
            formatTime={formatTime}
            filter={filter}
            setFilter={setFilter}
            loadingChats={loadingChats}
          />

          <ChatView
            selectedChat={selectedChat}
            isMobileView={isMobileView}
            handleBackToChatList={handleBackToChatList}
            messages={messages}
            loadingMessages={loadingMessages}
            messageText={messageText}
            onMessageChange={onMessageChange}
            showAttachMenu={showAttachMenu}
            setShowAttachMenu={setShowAttachMenu}
            formatTime={formatTime}
            messagesContainerRef={messagesContainerRef}
            messagesEndRef={messagesEndRef}
            renderTemplateMessage={renderTemplateMessage}
            sendSessionMessage={sendSessionMessage}
            imageInputRef={imageInputRef}
            docInputRef={docInputRef}
            handleMediaSend={handleMediaSend}
            mediaType={mediaType}
            setMediaType={setMediaType}
            sendingMessage={sendingMessage}
          />
        </div>
      )}
    </div>
  );
};

export default LiveChat;
